import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, RouterStateSnapshot, UrlTree } from '@angular/router';
import { from, Observable, of } from 'rxjs';
import { SessionQuery } from '@state/session/session.query';
import { AES_URL_STORAGE_KEY, ERROR_CODES, SESSION_ERROR_IDS, SESSION_STORAGE_KEY } from '@core/constants';
import { catchError, defaultIfEmpty, switchMap, timeout } from 'rxjs/operators';
import { SessionService } from '@state/session/session.service';
import { NavController } from '@ionic/angular';
import { Preferences } from '@capacitor/preferences';
import { SchedulerSessionService } from '@core/services/scheduler-session/scheduler-session.service';

@Injectable({
    providedIn: 'root'
})
export class AuthGuard {
    constructor(
        private navController: NavController,
        private sessionQuery: SessionQuery,
        private sessionService: SessionService,
        private schedulerSessionService: SchedulerSessionService ) {
    }

    canActivate(
        next: ActivatedRouteSnapshot,
        state: RouterStateSnapshot): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {

        // 1. Si ya está logueado en memoria (sesión activa verificada)
        if (this.sessionQuery.logged) {
            return true;
        }

        // 2. Intentar restaurar desde storage y validar contra el servidor
        return from(this.getStorage()).pipe(
            switchMap(([aesUrl, sessionId]: { value: string }[]) => {
                if (!aesUrl.value || !sessionId.value) {
                    // Modo Agendador: preservar el clientHubspotId de la query antes de
                    // redirigir a /login, para recuperarlo tras autenticarse.
                    const clientHubspotId = next.queryParamMap.get('clientHubspotId');
                    if (clientHubspotId) {
                        this.schedulerSessionService.saveClientId(clientHubspotId);
                    }

                    return this.redirectToLogin();
                }

                // Restaurar credenciales en el store
                this.sessionService.update({
                    aesUrl: aesUrl.value,
                    sessionId: sessionId.value
                });

                // 3. Validar contra el servidor con un refresh
                return this.sessionService.refreshConnection().pipe(
                    timeout(10000),
                    switchMap((response: any) => {
                        const errorId = this.extractErrorId(response);

                        // Error presente en el body que el interceptor no ha convertido en throw
                        // (p.ej. ERROR_OTP): la sesión no es utilizable y nadie más va a navegar
                        if (errorId !== null && errorId !== ERROR_CODES.ERROR_NONE) {
                            return from(this.handleExpiredSession(true));
                        }

                        return of(true);
                    }),
                    catchError(err => {
                        // Error de sesión confirmado: el DataInterceptor ya ha hecho
                        // systemLogout/systemReload y navegado a /login; solo limpiamos.
                        // (Con logout en curso el SessionInterceptor suele tragarse el
                        // error y este observable completa vacío → defaultIfEmpty)
                        if (SESSION_ERROR_IDS.includes(err) || err === 'system_reload') {
                            return from(this.handleExpiredSession(false));
                        }

                        // Timeout, sin red o error de negocio puntual: el servidor NO ha
                        // invalidado la sesión almacenada. Dejamos entrar y las siguientes
                        // peticiones (RefreshInterceptor) revalidarán; si borrásemos aquí,
                        // un arranque offline destruiría una sesión válida
                        return of(true);
                    }),
                    // Durante un logout el SessionInterceptor convierte el error en un
                    // completado sin emisión: sin esto canActivate acabaría en EmptyError
                    defaultIfEmpty(false)
                );
            }),
            catchError(() => this.redirectToLogin())
        );
    }

    /**
     * shouldNavigate=true cuando el error solo es visible en el body y el
     * DataInterceptor no ha ejecutado systemLogout: aquí nadie más redirige.
     * Con false se delega la navegación al logout del interceptor para evitar
     * que múltiples directivas enruten la app a la vez y colapsen el DOM.
     */
    private async handleExpiredSession(shouldNavigate: boolean): Promise<boolean> {
        await Preferences.remove({ key: SESSION_STORAGE_KEY });

        if (shouldNavigate) {
            await this.navController.navigateRoot('/login');
        }

        return false;
    }

    private redirectToLogin(): Observable<boolean> {
        this.navController.navigateRoot('/login');
        return of(false);
    }

    /**
     * Extrae el error del body del refresh. Tras el DataInterceptor el body es un
     * JSON en string, pero se soporta también XML crudo (error="261") por si la
     * respuesta no pasó por el interceptor. Devuelve null si no hay error numérico.
     */
    private extractErrorId(response: any): number | null {
        if (typeof response === 'string') {
            const attrMatch = response.match(/error="(\d+)"/);
            if (attrMatch) {
                return Number(attrMatch[1]);
            }

            try {
                const parsedError = Number(JSON.parse(response)?.error);
                return Number.isNaN(parsedError) ? null : parsedError;
            } catch (e) {
                return null;
            }
        }

        const objectError = Number(response?.error);
        return Number.isNaN(objectError) ? null : objectError;
    }

    private async getStorage(): Promise<{ value: string }[]> {
        return await Promise.all([
            Preferences.get({ key: AES_URL_STORAGE_KEY }),
            Preferences.get({ key: SESSION_STORAGE_KEY })
        ]);
    }
}
